import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { SearchInput } from '@/components/ui/SearchInput';
import { cn } from '@/lib/utils';
import {
	DEFAULT_SESSION_LIST_SORT,
	DEFAULT_SESSION_LIST_SORT_DIRECTION,
	SESSION_ADMIN_PAYMENT_OPTIONS,
	SESSION_ADMIN_SEARCH_HINT,
	SESSION_ADMIN_SEARCH_PLACEHOLDER,
	SESSION_ADMIN_SORT_DIRECTION_OPTIONS,
	SESSION_ADMIN_SORT_OPTIONS,
	SESSION_ADMIN_STATUS_OPTIONS,
	SESSION_ADMIN_TYPE_OPTIONS,
	type SessionListSortDirection,
	type SessionListSortField,
} from '../utils/sessionAdminFilters';

const FILTER_ALL_VALUE = '__all__';

const COMPACT_FIELD_CLASS = 'w-[10.5rem] shrink-0';
const COMPACT_DATE_FIELD_CLASS = 'w-[10.5rem] shrink-0';
const COMPACT_SELECT_TRIGGER_CLASS =
	'h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold text-zinc-600 shadow-sm';
const COMPACT_DATE_INPUT_CLASS =
	'block h-12 w-full rounded-full border-2 border-zinc-100 bg-white px-4 text-[15px] font-medium text-stone-900 shadow-sm transition-all outline-none focus:border-stone-900 focus:ring-2 focus:ring-inset focus:ring-stone-900/10 [color-scheme:light]';
const COMPACT_LABEL_CLASS =
	'mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-500';

interface CompactSelectFilterProps {
	label: string;
	paramKey: string;
	options: Array<{ value: string; label: string }>;
	value: string;
	onChange: (paramKey: string, value: string) => void;
	disabled?: boolean;
	hint?: string;
}

const CompactSelectFilter = ({
	label,
	paramKey,
	options,
	value,
	onChange,
	disabled = false,
	hint,
}: CompactSelectFilterProps) => (
	<div className={COMPACT_FIELD_CLASS}>
		<label className={COMPACT_LABEL_CLASS}>{label}</label>
		<Select
			value={value || FILTER_ALL_VALUE}
			onValueChange={(next) => onChange(paramKey, next)}
			disabled={disabled}
		>
			<SelectTrigger
				className={cn(
					COMPACT_SELECT_TRIGGER_CLASS,
					disabled && 'cursor-not-allowed opacity-60',
				)}
				hideTriggerIcon
			>
				<span
					className={cn(
						'truncate',
						!value && 'font-normal text-zinc-400',
					)}
				>
					{value
						? options.find((option) => option.value === value)
								?.label
						: 'Todos'}
				</span>
				<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
			</SelectTrigger>
			<SelectContent className="bg-white">
				<SelectItem value={FILTER_ALL_VALUE} indicator={false}>
					Todos
				</SelectItem>
				{options.map((option) => (
					<SelectItem
						key={option.value}
						value={option.value}
						indicator={false}
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
		{hint ? (
			<p className="mt-1 text-[10px] leading-snug text-zinc-400">
				{hint}
			</p>
		) : null}
	</div>
);

const SessionAdminSearchBar = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const dateFrom = searchParams.get('date_from') ?? '';
	const dateTo = searchParams.get('date_to') ?? '';

	const handleDateChange = useCallback(
		(paramKey: 'date_from' | 'date_to', value: string) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (!value) {
				nextParams.delete(paramKey);
			} else {
				nextParams.set(paramKey, value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const incompleteDateRange = (dateFrom && !dateTo) || (!dateFrom && dateTo);

	return (
		<div className="w-full space-y-1">
			<div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
				<div className="min-w-[16rem] flex-1 md:max-w-none">
					<label className={COMPACT_LABEL_CLASS}>Busca livre</label>
					<SearchInput
						size="lg"
						placeholder={SESSION_ADMIN_SEARCH_PLACEHOLDER}
						className="w-full md:max-w-none"
					/>
				</div>
				<div className={COMPACT_DATE_FIELD_CLASS}>
					<label className={COMPACT_LABEL_CLASS}>De</label>
					<input
						type="date"
						value={dateFrom}
						onChange={(event) =>
							handleDateChange('date_from', event.target.value)
						}
						className={COMPACT_DATE_INPUT_CLASS}
					/>
				</div>
				<div className={COMPACT_DATE_FIELD_CLASS}>
					<label className={COMPACT_LABEL_CLASS}>Até</label>
					<input
						type="date"
						value={dateTo}
						onChange={(event) =>
							handleDateChange('date_to', event.target.value)
						}
						className={COMPACT_DATE_INPUT_CLASS}
					/>
				</div>
			</div>
			<p className="text-[11px] leading-snug text-stone-400">
				{SESSION_ADMIN_SEARCH_HINT}
			</p>
			{incompleteDateRange ? (
				<p className="text-xs text-amber-700">
					Informe as duas datas do período para filtrar por intervalo.
				</p>
			) : null}
		</div>
	);
};

const SessionAdminFilters = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const status = searchParams.get('status') ?? '';
	const type = searchParams.get('type') ?? '';
	const payment = searchParams.get('payment') ?? '';
	const sort = searchParams.get('sort') ?? '';
	const sortDirection = searchParams.get('sort_direction') ?? '';

	const effectiveSort: SessionListSortField =
		sort === 'created_at' ? 'created_at' : DEFAULT_SESSION_LIST_SORT;
	const effectiveSortDirection: SessionListSortDirection =
		sortDirection === 'asc' ? 'asc' : DEFAULT_SESSION_LIST_SORT_DIRECTION;

	const handleSimpleFilterChange = useCallback(
		(paramKey: string, value: string) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (value === '' || value === FILTER_ALL_VALUE) {
				nextParams.delete(paramKey);
			} else {
				nextParams.set(paramKey, value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const handleSortChange = useCallback(
		(value: SessionListSortField) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (value === DEFAULT_SESSION_LIST_SORT) {
				nextParams.delete('sort');
			} else {
				nextParams.set('sort', value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const handleSortDirectionChange = useCallback(
		(value: SessionListSortDirection) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (value === DEFAULT_SESSION_LIST_SORT_DIRECTION) {
				nextParams.delete('sort_direction');
			} else {
				nextParams.set('sort_direction', value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div className="flex flex-wrap items-end gap-3">
					<CompactSelectFilter
						label="Status"
						paramKey="status"
						options={SESSION_ADMIN_STATUS_OPTIONS}
						value={status}
						onChange={handleSimpleFilterChange}
					/>
					<CompactSelectFilter
						label="Tipo"
						paramKey="type"
						options={SESSION_ADMIN_TYPE_OPTIONS}
						value={type}
						onChange={handleSimpleFilterChange}
					/>
					<CompactSelectFilter
						label="Pagamento"
						paramKey="payment"
						options={SESSION_ADMIN_PAYMENT_OPTIONS}
						value={payment}
						onChange={handleSimpleFilterChange}
					/>
				</div>
				<div className="flex flex-wrap items-end gap-3">
					<div className={COMPACT_FIELD_CLASS}>
						<label className={COMPACT_LABEL_CLASS}>
							Ordenar por
						</label>
						<Select
							value={effectiveSort}
							onValueChange={(value) =>
								handleSortChange(value as SessionListSortField)
							}
						>
							<SelectTrigger
								className={COMPACT_SELECT_TRIGGER_CLASS}
								hideTriggerIcon
							>
								<span className="truncate">
									{
										SESSION_ADMIN_SORT_OPTIONS.find(
											(option) =>
												option.value === effectiveSort,
										)?.label
									}
								</span>
								<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{SESSION_ADMIN_SORT_OPTIONS.map((option) => (
									<SelectItem
										key={option.value}
										value={option.value}
										indicator={false}
									>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className={COMPACT_FIELD_CLASS}>
						<label className={COMPACT_LABEL_CLASS}>Ordem</label>
						<Select
							value={effectiveSortDirection}
							onValueChange={(value) =>
								handleSortDirectionChange(
									value as SessionListSortDirection,
								)
							}
						>
							<SelectTrigger
								className={COMPACT_SELECT_TRIGGER_CLASS}
								hideTriggerIcon
							>
								<span className="truncate">
									{
										SESSION_ADMIN_SORT_DIRECTION_OPTIONS.find(
											(option) =>
												option.value ===
												effectiveSortDirection,
										)?.label
									}
								</span>
								<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
							</SelectTrigger>
							<SelectContent className="bg-white">
								{SESSION_ADMIN_SORT_DIRECTION_OPTIONS.map(
									(option) => (
										<SelectItem
											key={option.value}
											value={option.value}
											indicator={false}
										>
											{option.label}
										</SelectItem>
									),
								)}
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			{/*
			<div className="flex flex-wrap items-end gap-3">
				<CompactSelectFilter
					label="Escopo"
					paramKey="role"
					options={SESSION_ADMIN_SCOPE_ROLE_OPTIONS}
					value={scopeRole}
					onChange={(_, value) => handleScopeRoleChange(value)}
				/>
				{scopeRole === 'user' || scopeRole === 'professional' ? (
					<ScopeTargetPicker
						role={scopeRole}
						targetId={targetId || null}
						onSelect={handleTargetSelect}
						onClear={handleClearScope}
					/>
				) : null}
				{scopeRole === 'professional' && targetId ? (
					<PatientScopePicker
						professionalId={targetId}
						patientId={patientId || null}
						onSelect={handlePatientSelect}
						onClear={handleClearPatient}
					/>
				) : null}
			</div>
			*/}
		</div>
	);
};

export { SessionAdminFilters, SessionAdminSearchBar };
